import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

const siteChipColors = [
  { backgroundColor: '#E3F2FD', color: '#0D47A1', borderColor: '#BBDEFB' },
  { backgroundColor: '#E8F5E9', color: '#1B5E20', borderColor: '#C8E6C9' },
  { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#FFE0B2' },
  { backgroundColor: '#F3E5F5', color: '#4A148C', borderColor: '#E1BEE7' },
  { backgroundColor: '#E0F2F1', color: '#004D40', borderColor: '#B2DFDB' },
  { backgroundColor: '#FCE4EC', color: '#880E4F', borderColor: '#F8BBD0' },
];

const formatMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return String(v ?? '0');
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const SystemInvoiceSitesAndServiceTables = ({ sitesData = [], serviceItemsData = [] }) => {
  const totalInvoiceAmount = useMemo(() => {
    const parseNumber = (v) => {
      if (v == null) {
        return 0;
      }
      const n = Number(String(v).replace(/,/g, '').trim());
      return Number.isFinite(n) ? n : 0;
    };
    return (serviceItemsData || []).reduce((sum, it) => {
      const qty = parseNumber(it?.invoiced_qty ?? it?.invoicedQty);
      const unit = parseNumber(it?.unit_price ?? it?.unitPrice);
      return sum + qty * unit;
    }, 0);
  }, [serviceItemsData]);

  return (
    <>
      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: '16px',
          border: '1px solid #EAECF0',
          background:
            'linear-gradient(135deg, rgba(189, 29, 35, 0.06) 0%, rgba(25, 118, 210, 0.05) 100%)',
          boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontFamily: 'Inter',
                fontWeight: 800,
                color: '#101828',
              }}
            >
              Sites
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#667085', mt: 0.25 }}>
              Sites included in this invoice
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${sitesData.length} site${sitesData.length === 1 ? '' : 's'}`}
            sx={{
              backgroundColor: '#BD1D23',
              color: '#fff',
              fontWeight: 800,
              borderRadius: '999px',
            }}
          />
        </Box>

        {sitesData.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {sitesData.map((site, index) => {
              const chipColor = siteChipColors[index % siteChipColors.length];
              return (
                <Chip
                  key={`${site}-${index}`}
                  size="small"
                  label={site}
                  sx={{
                    ...chipColor,
                    border: `1px solid ${chipColor.borderColor}`,
                    borderRadius: '10px',
                    fontWeight: 800,
                    letterSpacing: 0.2,
                    boxShadow: '0px 2px 6px rgba(16, 24, 40, 0.08)',
                  }}
                />
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: '1px dashed #D0D5DD',
              backgroundColor: 'rgba(255,255,255,0.75)',
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#667085' }}>
              No sites found for this invoice.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 600,
            mb: 2,
            color: '#192126',
          }}
        >
          Service Items
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: '1px solid #E8E9E9',
            borderRadius: '8px',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                <TableCell sx={{ fontWeight: 700, color: '#344054', width: 56 }}>Sr</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Code</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>PO Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Claimed Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Remaining Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Invoiced Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(serviceItemsData || []).map((it, idx) => {
                const qty = Number(it?.invoiced_qty ?? 0) || 0;
                const unit =
                  Number(
                    String(it?.unit_price ?? 0)
                      .replace(/,/g, '')
                      .trim()
                  ) || 0;
                const amount = qty * unit;
                return (
                  <TableRow key={`${it?.item_code || 'item'}-${idx}`}>
                    <TableCell sx={{ color: '#667085' }}>{idx + 1}</TableCell>
                    <TableCell>{it?.item_code ?? '—'}</TableCell>
                    <TableCell>{it?.item_type ?? '—'}</TableCell>
                    <TableCell>{it?.po_qty ?? 0}</TableCell>
                    <TableCell>{it?.claimed_qty ?? 0}</TableCell>
                    <TableCell>{it?.remaining_qty ?? 0}</TableCell>
                    <TableCell>{it?.invoiced_qty ?? 0}</TableCell>
                    <TableCell>{it?.unit_price ?? '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#101828' }}>
                      {formatMoney(amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!serviceItemsData || serviceItemsData.length === 0) && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ color: '#667085' }}>
                    No service items found.
                  </TableCell>
                </TableRow>
              )}
              {serviceItemsData && serviceItemsData.length > 0 && (
                <TableRow
                  sx={{
                    background:
                      'linear-gradient(90deg, rgba(189, 29, 35, 0.10) 0%, rgba(16, 185, 129, 0.10) 100%)',
                  }}
                >
                  <TableCell colSpan={8} sx={{ fontWeight: 800, color: '#101828' }}>
                    Total Invoice Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 900, color: '#101828' }}>
                    {formatMoney(totalInvoiceAmount)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default SystemInvoiceSitesAndServiceTables;
